import { reminderTime } from 'src/utils/reminder-time';
import { useState } from 'react';
import {
  Box,
  Card,
  Chip,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import { EventBusy, Group } from '@mui/icons-material';
import { handleApiCall } from 'src/helper/call_api_helper';
import Swal from 'sweetalert2';
import RecipientsModal from './RecipientsModal';

const ActivityLogsTable = ({ activityLogs = [], schedules = [], dataSource, refresh }) => {
  const [schedulePage, setSchedulePage] = useState(0);
  const [scheduleLimit, setScheduleLimit] = useState(10);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);

  const [openRecipientsModal, setOpenRecipientsModal] = useState(false);
  const [selectedScheduleId, setSelectedScheduleId] = useState<string | null>(
    null
  );

  const handlePageChange = (event, newPage) => setPage(newPage);
  const handleLimitChange = (event) => { setLimit(parseInt(event.target.value)); setPage(0); };

  const handleCancle = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: '',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, cancel it!'
    });

    if (!result.isConfirmed) return;

    await handleApiCall(
      () => dataSource.cancel(id),
      'Schedule Notification Canceled Successfully',
      () => {
        refresh();
      },
      (error) => {}
    );
  };

  const handleShowRecipients = (scheduleId) => {
    setSelectedScheduleId(scheduleId);
    setOpenRecipientsModal(true);
  };

  return (
    <>
      <Card>
        <Box
          sx={{
            padding: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            Activity Logs
          </Typography>
        </Box>
        <Divider />

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {activityLogs
                .slice(page * limit, page * limit + limit)
                .map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>{log.title}{log.eventTitle && <Typography variant="caption" display="block">{log.eventTitle}{log.eventId ? ` · Event #${log.eventId}` : ''}</Typography>}</TableCell>
                    <TableCell>{log.description}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box p={2}>
          <TablePagination
            component="div"
            count={activityLogs.length}
            page={page}
            rowsPerPage={limit}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleLimitChange}
          />
        </Box>
      </Card>
      <Card sx={{ mt: 2 }}>
        <Box
          sx={{
            padding: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            Scheduled Notifications
          </Typography>
        </Box>
        <Divider />

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Body</TableCell>
                <TableCell>Schedule At</TableCell>
                {/* <TableCell>timezone</TableCell> */}
                <TableCell>status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {schedules
                .slice(schedulePage * scheduleLimit, schedulePage * scheduleLimit + scheduleLimit)
                .map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>{log.title}{log.eventTitle && <Typography variant="caption" display="block">{log.eventTitle}{log.eventId ? ` · Event #${log.eventId}` : ''}</Typography>}</TableCell>
                    <TableCell>{log.data}</TableCell>
                    <TableCell>{reminderTime(log.scheduledAt, log.eventTimezone)}{!log.eventTimezone && <Typography variant="caption" display="block">Event Timezone Unavailable</Typography>}</TableCell>
                    {/* <TableCell>
                      Eastern Standard Time (EST/EDT) - UTC-5 / UTC-4
                    </TableCell> */}
                    <TableCell>
                      {' '}
                      <Chip
                        label={log.status === 'LEGACY_UNKNOWN' ? 'Legacy — unverified' : `Push/inbox: ${log.status}`}
                        color={log.status === 'SENT' ? 'success' : log.status === 'FAILED' ? 'error' : log.status === 'PENDING' ? 'warning' : 'default'}
                        size="small"
                        variant="outlined"
                        sx={{
                          textTransform: 'capitalize',
                          fontWeight: 500,
                          fontSize: '0.75rem'
                        }}
                      />
                      {log.emailStatus && <Chip label={`Email: ${log.emailStatus}`} size="small" variant="outlined" sx={{ ml: 1 }} />}
                      {log.dispatchStartedAt && <Typography variant="caption" display="block">Delivery started; cancellation unavailable.</Typography>}
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Show Recipients" arrow>
                        <IconButton
                          color="primary"
                          onClick={() => handleShowRecipients(log.id)}
                          size="small"
                        >
                          <Group fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Cancel Notification" arrow>
                        <IconButton
                          color="error"
                          disabled={log.status !== 'PENDING' || Boolean(log.dispatchStartedAt)}
                          onClick={() => handleCancle(log.id)}
                          size="small"
                        >
                          <EventBusy fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box p={2}>
          <TablePagination
            component="div"
            count={schedules.length}
            page={schedulePage}
            rowsPerPage={scheduleLimit}
            onPageChange={(_, value) => setSchedulePage(value)}
            onRowsPerPageChange={(event) => { setScheduleLimit(Number(event.target.value)); setSchedulePage(0); }}
          />
        </Box>

        <RecipientsModal
          dataSource={dataSource}
          open={openRecipientsModal}
          onClose={() => setOpenRecipientsModal(false)}
          scheduleId={selectedScheduleId}
        />
      </Card>
    </>
  );
};

export default ActivityLogsTable;
